import React, { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BadgeCheck, ShieldQuestion, Crown, Video, Search, ExternalLink, Phone, MapPin, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROLE_LABEL = { beauty_creator: "Beauty Creator", affiliator: "Affiliator" };

const waLink = (num) => {
  const d = (num || "").replace(/\D/g, "");
  if (!d) return undefined;
  return `https://wa.me/${d.startsWith("0") ? "62" + d.slice(1) : d}`;
};

const StatusPill = ({ data, platform }) => {
  if (data?.verified) {
    return (
      <a href={data.handle && /^https?:\/\//i.test(data.handle) ? data.handle : undefined} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-semibold hover:underline" data-testid={`verif-${platform}-yes`}>
        <BadgeCheck className="w-3.5 h-3.5" /> Terverifikasi
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-showty-cream text-muted-foreground px-2.5 py-1 text-xs font-medium" data-testid={`verif-${platform}-no`}>
      <ShieldQuestion className="w-3.5 h-3.5" /> Belum
    </span>
  );
};

export default function VerificationsPanel() {
  const [data, setData] = useState({ members: [], counts: {} });
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const rf = role === "all" ? "" : `?role_filter=${role}`;
    api.get(`/verifications${rf}`).then((r) => setData(r.data)).catch(() => {});
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const list = data.members.filter((m) => {
    const s = q.trim().toLowerCase();
    return !s || (m.name || "").toLowerCase().includes(s) || (m.email || "").toLowerCase().includes(s);
  });

  const exportExcel = async () => {
    try {
      const rf = role === "all" ? "" : `?role_filter=${role}`;
      const res = await api.get(`/verifications/export${rf}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "showtynion-verifikasi-akun.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export Excel"); }
  };

  return (
    <div data-testid="verifications-panel">
      <p className="text-sm text-muted-foreground mb-4">Pantauan CEO: status verifikasi akun TikTok & Shopee tiap member. Verifikasi otomatis saat member ikut campaign dengan link akun valid.</p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: "Total Member", value: data.counts.total ?? 0, icon: Crown, tone: "text-showty-navy" },
          { label: "TikTok terverifikasi", value: data.counts.tiktok_verified ?? 0, icon: BadgeCheck, tone: "text-showty-red" },
          { label: "Shopee terverifikasi", value: data.counts.shopee_verified ?? 0, icon: BadgeCheck, tone: "text-showty-red" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-white p-4" data-testid={`verif-stat-${s.label}`}>
            <s.icon className={`w-5 h-5 ${s.tone}`} />
            <p className="mt-2 font-display text-2xl text-showty-navy">{s.value}</p>
            <p className="text-[11px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="verif-role-filter">
          {[{ id: "all", label: "Semua", icon: Crown }, { id: "beauty_creator", label: "Creators", icon: Crown }, { id: "affiliator", label: "Affiliators", icon: Video }].map((r) => (
            <button key={r.id} onClick={() => setRole(r.id)} data-testid={`verif-filter-${r.id}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ${role === r.id ? "bg-showty-navy text-white" : "text-muted-foreground hover:text-showty-navy"}`}>
              <r.icon className="w-4 h-4" /> {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button onClick={exportExcel} className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white shrink-0" data-testid="verif-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel</Button>
          <div className="relative flex-1 sm:w-72">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-10 rounded-full" placeholder="Cari nama / email member…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="verif-search" />
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white/50 py-14 text-center text-sm text-muted-foreground" data-testid="verif-empty">Belum ada member</div>
      ) : (
        <div className="rounded-2xl border border-border bg-white overflow-x-auto" data-testid="verif-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-showty-cream text-left text-showty-navy">
                <th className="px-4 py-3 font-semibold">Member</th>
                <th className="px-4 py-3 font-semibold">Peran</th>
                <th className="px-4 py-3 font-semibold">WhatsApp</th>
                <th className="px-4 py-3 font-semibold">Kota</th>
                <th className="px-4 py-3 font-semibold">TikTok</th>
                <th className="px-4 py-3 font-semibold">Shopee</th>
                <th className="px-4 py-3 font-semibold">Instagram</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.user_id} className="border-t border-border" data-testid={`verif-row-${m.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {m.picture ? <img src={m.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-showty-pink flex items-center justify-center text-showty-red text-xs font-semibold">{(m.name || m.email || "U")[0]}</div>}
                      <div className="min-w-0"><p className="font-medium text-showty-navy truncate">{m.name || "—"}</p><p className="text-xs text-muted-foreground truncate">{m.email}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs rounded-full bg-showty-pink/40 px-2 py-0.5 text-showty-navy">{ROLE_LABEL[m.role]}</span></td>
                  <td className="px-4 py-3">
                    {m.whatsapp ? <a href={waLink(m.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-green-700 hover:underline" data-testid={`verif-wa-${m.user_id}`}><Phone className="w-3 h-3" /> {m.whatsapp}</a> : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {m.city ? <span className="inline-flex items-center gap-1 text-xs text-showty-navy" data-testid={`verif-city-${m.user_id}`}><MapPin className="w-3 h-3 text-showty-red" /> {m.city}</span> : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3"><StatusPill data={m.tiktok} platform="tiktok" /></td>
                  <td className="px-4 py-3"><StatusPill data={m.shopee} platform="shopee" /></td>
                  <td className="px-4 py-3">
                    {m.instagram?.handle ? <a href={/^https?:\/\//i.test(m.instagram.handle) ? m.instagram.handle : undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-showty-red hover:underline"><ExternalLink className="w-3 h-3" /> Link</a> : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
